/**
 * AnswerCard — a grounded /ask answer (CONTRACT §3 ask).
 *
 * Part chips carry role as icon + label + colour (matching the 'answer'
 * overlay). Hop numbers match the badges drawn in 3D. Hovering a chip or a
 * citation lights that part; clicking selects it.
 *
 * Props:
 *   answer    { question, phase, data, reason }
 *   tree      buildTree() result
 *   onSelect  (id) => void
 *   onHover   (id|null) => void
 *   onRetry   () => void
 *   onClose   () => void
 */

import { memo, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { answerRole } from '../diagnosticPalette.js';
import { ASK_TIMEOUT_MS } from './usePartKnowledge.js';
import './KnowledgeCards.css';

const KIND_LABEL = {
  summary: 'Summary', spec: 'Spec', failure_mode: 'Failure mode', task: 'Task', fault: 'Fault',
};
/**
 * The backend may end an answer with a multi-line "Safety:" block. Pull it out
 * so it renders as a distinct list; everything else keeps its newlines.
 */
function splitSafety(text) {
  const src = String(text ?? '');
  const idx = src.search(/(^|\n)[ \t]*Safety:/i);
  if (idx < 0) return { body: src, safety: [] };
  const body = src.slice(0, idx).trim();
  let safety = src.slice(idx).replace(/^\s*Safety:\s*/i, '')
    .split('\n')
    .map((l) => l.replace(/^\s*[-•*]\s*/, '').trim())
    .filter(Boolean);
  // One run-on paragraph: one item per sentence reads as a checklist.
  if (safety.length === 1) {
    safety = safety[0].split(/(?<=\.)\s+(?=[A-Z])/).map((l) => l.trim()).filter(Boolean);
  }
  return { body, safety };
}

/** Honest progress for a slow local model: elapsed seconds, no fake percentage. */
function Thinking() {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="kc__loading" role="status">
      <div className="kc__progress" aria-hidden="true"><span /></div>
      <span>
        Thinking with local model
        <span aria-hidden="true"> · {secs} s</span>
      </span>
      {secs >= 8 && (
        <span className="kc__text--dim">
          Usually 15 s; the first answer after a backend restart can take about 35 s.
        </span>
      )}
    </div>
  );
}

const RELATION_LABEL = {
  PART_OF: 'part of', CONNECTS: 'connects', HAS_FAILURE_MODE: 'failure mode', SUSPECTS: 'suspect',
};

function AnswerCardImpl({ answer, tree, onSelect, onHover, onRetry, onClose }) {
  const { question, phase, data, reason } = answer;
  const known = (id) => Boolean(tree?.byId?.has(id));
  const hoverProps = (id) => ({
    onMouseEnter: () => known(id) && onHover(id),
    onMouseLeave: () => onHover(null),
    onFocus: () => known(id) && onHover(id),
    onBlur: () => onHover(null),
  });

  return (
    <section className="kc kc--answer" aria-label="Answer" aria-busy={phase === 'loading'}>
      <header className="kc__head">
        <div className="kc__head-main">
          <span className="kc__kicker">
            Answer
            {phase === 'ready' && data && (
              <span className={`kc__badge${data.used_llm ? ' is-llm' : ''}`}>
                {data.used_llm ? 'Local model' : 'Template answer'}
              </span>
            )}
          </span>
          <h3 className="kc__title kc__title--question">{question}</h3>
        </div>
        <button type="button" className="kc__close" onClick={onClose} aria-label="Close answer (Esc)" title="Close (Esc)">
          <X size={15} aria-hidden="true" />
        </button>
      </header>

      <div className="kc__body" aria-live="polite">
        {phase === 'loading' && <Thinking />}

        {phase === 'error' && (
          <div className="kc__state" role="status">
            <p className="kc__state-title">
              {reason === 'timeout'
                ? `No answer within ${Math.round(ASK_TIMEOUT_MS / 1000)} s`
                : 'Knowledge service unavailable'}
            </p>
            <p className="kc__text kc__text--dim">
              Drill-down commands (&ldquo;open …&rdquo;, &ldquo;deeper&rdquo;, &ldquo;back&rdquo;) still work offline.
            </p>
            <button type="button" className="exview__btn" onClick={onRetry}>Try again</button>
          </div>
        )}

        {phase === 'ready' && data && (
          <>
            {(() => {
              const { body, safety } = splitSafety(data.answer);
              return (
                <>
                  {body && <p className="kc__answer">{body}</p>}
                  {safety.length > 0 && (
                    <div className="kc__safety" role="note" aria-label="Safety">
                      <div className="kc__safety-title">Safety</div>
                      <ul>{safety.map((l) => <li key={l}>{l}</li>)}</ul>
                    </div>
                  )}
                </>
              );
            })()}

            {data.part_ids.length > 0 && (
              <ul className="kc__chips" aria-label="Parts in this answer">
                {data.part_ids.map((p) => {
                  const r = answerRole(p.role);
                  return (
                    <li key={`${p.role}-${p.cad_id}`}>
                      <button
                        type="button"
                        className="kc__chip kc__chip--role"
                        style={{ borderColor: r.color }}
                        onClick={() => known(p.cad_id) && onSelect(p.cad_id)}
                        disabled={!known(p.cad_id)}
                        {...hoverProps(p.cad_id)}
                      >
                        <span className="kc__chip-role" style={{ color: r.color }}>
                          <span aria-hidden="true">{r.icon}</span> {r.label}
                        </span>
                        <span>{p.name ?? tree?.byId?.get(p.cad_id)?.name ?? p.cad_id}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {data.hops.length > 0 && (
              <>
                <h4 className="kc__h">Reasoning path</h4>
                <ol className="kc__hops">
                  {data.hops.map((h) => (
                    <li key={`${h.step}-${h.cad_id}`}>
                      <span className="kc__hop" aria-hidden="true">{h.step}</span>
                      <button
                        type="button"
                        className="kc__link"
                        onClick={() => known(h.cad_id) && onSelect(h.cad_id)}
                        disabled={!known(h.cad_id)}
                        {...hoverProps(h.cad_id)}
                      >
                        {h.name ?? h.cad_id}
                      </button>
                      <span className="kc__rel">{RELATION_LABEL[h.relation] ?? h.relation}</span>
                      {h.text && <span className="kc__text kc__text--dim kc__hop-text">{h.text}</span>}
                    </li>
                  ))}
                </ol>
              </>
            )}

            {data.citations.length > 0 && (
              <>
                <h4 className="kc__h">Citations</h4>
                <ul className="kc__cites">
                  {data.citations.map((c, i) => (
                    <li key={`${c.id}-${i}`}>
                      <button
                        type="button"
                        className="kc__cite"
                        onClick={() => known(c.cad_id) && onSelect(c.cad_id)}
                        {...hoverProps(c.cad_id)}
                      >
                        {/* Answers cite as [n], 1-based into this list. */}
                        <span className="kc__cite-kind">
                          <span className="kc__cite-n">[{i + 1}]</span> {KIND_LABEL[c.kind] ?? c.kind}
                        </span>
                        <span className="kc__cite-part">{tree?.byId?.get(c.cad_id)?.name ?? c.cad_id}</span>
                        <span className="kc__cite-text">{c.text}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      <footer className="kc__foot">
        {data?.provenance || 'Answers are grounded only on cited part knowledge'} · Esc closes
      </footer>
    </section>
  );
}

export const AnswerCard = memo(AnswerCardImpl);
export default AnswerCard;
